import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  'Cédula de ciudadanía',
  'Pasaporte',
  'NUIP',
  'Tarjeta de identidad',
  'Cédula Extranjería',
  'Permiso por protección temporal',
  'Permiso especial de Permanencia'
];

const ROLES = [
  'Emprendedor',
  'Empresario',
  'Estudiante',
  'Docente',
  'Academia – universidad',
  'Aliado del Ecosistema',
  'Aliado del Gobierno',
  'Gremio',
  'Otro'
];

const LGBTIQ_OPTIONS = [
  'Seleccione...',
  'Prefiero no decir',
  'Sí',
  'No'
];

export default function EditarPerfil() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    document_type: '',
    document_id: '',
    first_name: '',
    second_name: '',
    first_lastname: '',
    second_lastname: '',
    email: '',
    phone: '',
    role: '',
    position: '',
    is_migrant: false,
    is_lgbtiq: '',
    is_caregiver: false,
    is_veteran: false
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      setFormData({
        document_type: user.document_type || '',
        document_id: user.document_id || '',
        first_name: user.first_name || '',
        second_name: user.second_name || '',
        first_lastname: user.first_lastname || '',
        second_lastname: user.second_lastname || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || '',
        position: user.position || '',
        is_migrant: user.is_migrant || false,
        is_lgbtiq: user.is_lgbtiq || '',
        is_caregiver: user.is_caregiver || false,
        is_veteran: user.is_veteran || false
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.document_type || !formData.document_id || !formData.first_name || 
        !formData.first_lastname || !formData.phone || !formData.role || !formData.position) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar el perfil');
      }

      const updatedUser = await response.json();
      if (updateUser) {
        updateUser(updatedUser);
      }
      toast.success('Perfil actualizado exitosamente');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const RadioOption = ({ name, value, checked, onChange, label }) => (
    <button
      type="button"
      onClick={() => onChange({ target: { name, value: value, type: 'checkbox', checked: value } })}
      className={`flex-1 px-4 py-3 text-sm font-medium border transition-colors ${
        checked === value
          ? 'bg-[#0F172A] text-white border-[#0F172A]'
          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
      } ${label === 'No' ? 'rounded-l-lg border-r-0' : 'rounded-r-lg'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                {user?.requires_data_update ? 'Completar Datos de Cuenta' : 'Editar Perfil'}
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className={`flex items-center gap-2 text-slate-300 hover:text-white transition-colors ${user?.requires_data_update ? 'invisible pointer-events-none' : ''}`}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {user?.requires_data_update && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-5 flex gap-3" data-testid="legacy-update-banner">
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900 mb-1">Bienvenido(a) de nuevo al Registro Único</p>
              <p className="text-amber-800">
                Hemos migrado su cuenta desde nuestra base de datos histórica.
                Por favor revise y <strong>actualice todos sus datos</strong> antes de continuar.
                Una vez los confirme y guarde, podrá usar la plataforma normalmente.
              </p>
            </div>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 md:p-8">
          <div className="border-b-4 border-[#0F172A] pb-4 mb-6">
            <h2 className="text-xl font-bold text-[#0F172A]">{user?.requires_data_update ? 'ACTUALIZAR MIS DATOS' : 'EDITAR MI PERFIL'}</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Document Type and Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Tipo de Identificación <span className="text-red-500">*</span>:
                </label>
                <select
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                >
                  <option value="">Seleccione...</option>
                  {DOCUMENT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Número de Identificación <span className="text-red-500">*</span>:
                </label>
                <input
                  type="text"
                  name="document_id"
                  value={formData.document_id}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                />
              </div>
            </div>

            {/* Row 2: Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Primer nombre <span className="text-red-500">*</span>:
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Segundo nombre :
                </label>
                <input
                  type="text"
                  name="second_name"
                  value={formData.second_name}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                />
              </div>
            </div>

            {/* Row 3: Last Names */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Primer apellido <span className="text-red-500">*</span>:
                </label>
                <input
                  type="text"
                  name="first_lastname"
                  value={formData.first_lastname}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Segundo apellido :
                </label>
                <input
                  type="text"
                  name="second_lastname"
                  value={formData.second_lastname}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                />
              </div>
            </div>

            {/* Row 4: Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Correo electrónico <span className="text-red-500">*</span>:
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Teléfono celular (10 dígitos)<span className="text-red-500">*</span>:
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                />
              </div>
            </div>

            {/* Row 5: Role and Position */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Rol <span className="text-red-500">*</span>:
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                >
                  <option value="">Seleccione...</option>
                  {ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  Cargo <span className="text-red-500">*</span>:
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-slate-50"
                  required
                />
              </div>
            </div>

            {/* Row 6: Migrant and LGBTIQ+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-2">
                  ¿Usted es una persona migrante? <span className="text-red-500">*</span>:
                </label>
                <div className="flex">
                  <RadioOption
                    name="is_migrant"
                    value={false}
                    checked={formData.is_migrant}
                    onChange={handleChange}
                    label="No"
                  />
                  <RadioOption
                    name="is_migrant"
                    value={true}
                    checked={formData.is_migrant}
                    onChange={handleChange}
                    label="Si"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-1">
                  ¿Usted hace parte de los sectores sociales LGBTIQ+ y/o personas que se reconocen con orientaciones sexuales e identidades de género diversas? <span className="text-red-500">*</span>:
                </label>
                <select
                  name="is_lgbtiq"
                  value={formData.is_lgbtiq}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-slate-50"
                >
                  {LGBTIQ_OPTIONS.map(opt => (
                    <option key={opt} value={opt === 'Seleccione...' ? '' : opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 7: Caregiver and Veteran */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-red-600 mb-2">
                  ¿Considera usted que realiza trabajos de cuidado directo o indirecto, remunerados o no remunerado? <span className="text-red-500">*</span>:
                </label>
                <div className="flex">
                  <RadioOption
                    name="is_caregiver"
                    value={false}
                    checked={formData.is_caregiver}
                    onChange={handleChange}
                    label="No"
                  />
                  <RadioOption
                    name="is_caregiver"
                    value={true}
                    checked={formData.is_caregiver}
                    onChange={handleChange}
                    label="Si"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-red-600 mb-2">
                  ¿Es usted un Veterano de la Fuerza Pública o un beneficiario de un Veterano de la Fuerza Pública? <span className="text-red-500">*</span>:
                </label>
                <div className="flex">
                  <RadioOption
                    name="is_veteran"
                    value={false}
                    checked={formData.is_veteran}
                    onChange={handleChange}
                    label="No"
                  />
                  <RadioOption
                    name="is_veteran"
                    value={true}
                    checked={formData.is_veteran}
                    onChange={handleChange}
                    label="Si"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {loading ? 'GUARDANDO...' : 'ACTUALIZAR'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="w-full px-8 py-4 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 transition-colors"
              >
                VOLVER
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
